import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import StorefrontLayout from "../components/storefront/Layout";
import ProductCard from "../components/storefront/ProductCard";
import FitmentAssistButton from "../components/storefront/FitmentAssistButton";
import Breadcrumbs, { breadcrumbJsonLd } from "../components/storefront/Breadcrumbs";
import { api } from "../lib/api";
import { useSeo } from "../lib/seo";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const CONTROL_SEL = "h-9 px-2 text-xs bg-transparent border border-white/15 rounded-sm text-zinc-300 focus:outline-none focus:border-red-500 cursor-pointer";

const FEATURE_FILTERS = [
  { key: "heated", label: "Heated" },
  { key: "blind_spot", label: "Blind spot" },
  { key: "auto_dimming", label: "Auto-dimming" },
  { key: "with_camera", label: "With camera" },
  { key: "hud", label: "HUD" },
  { key: "rain_sensor", label: "Rain sensor" },
  { key: "with_backing_plate", label: "w/BackingPlate" },
];

const POSITION_FILTERS = [
  { key: "left", label: "Left" },
  { key: "right", label: "Right" },
  { key: "pair", label: "Pair" },
  { key: "front", label: "Front" },
  { key: "rear", label: "Rear" },
  { key: "panoramic", label: "Panoramic" },
  { key: "upper-left", label: "Upper left" },
  { key: "upper-right", label: "Upper right" },
  { key: "lower-left", label: "Lower left" },
  { key: "lower-right", label: "Lower right" },
];

const FITMENT_TYPE_FILTERS = [
  { key: "oem-style", label: "OEM-style" },
  { key: "aftermarket", label: "Aftermarket" },
];

export default function Shop() {
  const { category, vehicleId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const vehicleLabel = searchParams.get("label");

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const pageParam = parseInt(searchParams.get("page"), 10);
  const page = Number.isFinite(pageParam) && pageParam > 1 ? pageParam : 1;
  const [perPage, setPerPage] = useState(() => {
    const s = Number(sessionStorage.getItem("lecis_perpage"));
    return [12, 24, 48, 96].includes(s) ? s : 24;
  });
  const [sort, setSort] = useState(() => sessionStorage.getItem("lecis_sort") || "newest");
  const [filters, setFilters] = useState({
    category: category || "",
    position: "",
    fitment_type: "",
    heated: undefined,
    blind_spot: undefined,
    auto_dimming: undefined,
    with_camera: undefined,
    with_backing_plate: undefined,
  });

  useEffect(() => { sessionStorage.setItem("lecis_perpage", String(perPage)); }, [perPage]);
  useEffect(() => { sessionStorage.setItem("lecis_sort", sort); }, [sort]);

  useEffect(() => {
    setFilters((f) => ({ ...f, category: category || "" }));
  }, [category]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (vehicleId) p.set("vehicle_id", vehicleId);
    if (filters.category) p.set("category", filters.category);
    if (filters.position) p.set("position", filters.position);
    if (filters.fitment_type) p.set("fitment_type", filters.fitment_type);
    FEATURE_FILTERS.forEach(({ key }) => {
      if (filters[key]) p.set(key, "true");
    });
    return p.toString();
  }, [vehicleId, filters]);

  // Reset to page 1 whenever the result set (filters / sort / page-size) changes.
  // Handled in the change handlers below via resetPage() so deep-links to
  // ?page=N are respected on initial load.

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams(query);
    p.set("sort", sort);
    p.set("limit", String(perPage));
    p.set("page", String(page));
    api.get(`/products?${p.toString()}`).then(({ data }) => {
      setProducts(data.products);
      setTotal(data.total ?? data.products.length);
      setTotalPages(data.total_pages ?? 1);
      setLoading(false);
    });
  }, [query, sort, perPage, page]);

  const resetPage = () => setSearchParams((prev) => {
    const sp = new URLSearchParams(prev);
    sp.delete("page");
    return sp;
  }, { replace: true });

  const goToPage = (n) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev);
      if (n <= 1) sp.delete("page"); else sp.set("page", String(n));
      return sp;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const toggleFeature = (key) => { setFilters((f) => ({ ...f, [key]: f[key] ? undefined : true })); resetPage(); };
  const setExclusive = (key, val) => { setFilters((f) => ({ ...f, [key]: f[key] === val ? "" : val })); resetPage(); };

  const routeCategory = category || "";
  const hasActiveFilters = !!(
    filters.position ||
    filters.fitment_type ||
    FEATURE_FILTERS.some((f) => filters[f.key]) ||
    (filters.category && !routeCategory)
  );
  const clearFilters = () => {
    setFilters({
      category: routeCategory, position: "", fitment_type: "",
      heated: undefined, blind_spot: undefined, auto_dimming: undefined,
      with_camera: undefined, with_backing_plate: undefined,
    });
    resetPage();
  };
  const changeSort = (v) => { setSort(v); resetPage(); };
  const changePerPage = (v) => { setPerPage(v); resetPage(); };

  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);

  const { exactProducts, relatedProducts } = useMemo(() => {
    if (!vehicleId) return { exactProducts: [], relatedProducts: products };
    return {
      exactProducts: products.filter((p) => p.exact_fit),
      relatedProducts: products.filter((p) => !p.exact_fit),
    };
  }, [products, vehicleId]);

  const heading = vehicleLabel
    ? `Parts for ${vehicleLabel}`
    : category === "side-mirror"
      ? "Side Mirrors"
      : category === "windshield"
        ? "Windshields"
        : category === "backglass"
          ? "Backglass"
          : category === "sunroof"
            ? "Sunroof"
            : "Shop all parts";

  const crumbs = [
    { label: "Home", to: "/" },
    { label: "Shop", to: "/shop" },
    ...(category ? [{ label: heading, to: `/shop/${category}` }] : []),
    ...(vehicleLabel ? [{ label: vehicleLabel }] : []),
  ];
  const seoTitle = category
    ? `${heading} for Exotic Vehicles | Lecis Auto Glass`
    : vehicleLabel
      ? `${heading} | Lecis Auto Glass`
      : "Shop Auto Glass — Side Mirrors & Windshields | Lecis Auto Glass";
  const seoDesc = vehicleLabel
    ? `Compatible side mirrors and windshields for ${vehicleLabel}. OEM-style and aftermarket, fitment-verified.`
    : `Premium ${(category || "side mirrors and windshields").replace("-", " ")} for Ferrari, Lamborghini, Porsche, Bentley & more. Free US shipping.`;
  const basePath = category ? `/shop/${category}` : "/shop";
  const isBrowse = !vehicleId;
  const canonicalPath = isBrowse && page > 1 ? `${basePath}?page=${page}` : basePath;
  const prevPath = isBrowse && page > 1 ? (page === 2 ? basePath : `${basePath}?page=${page - 1}`) : null;
  const nextPath = isBrowse && page < totalPages ? `${basePath}?page=${page + 1}` : null;
  useSeo({
    title: seoTitle,
    description: seoDesc,
    canonicalPath,
    prevPath,
    nextPath,
    jsonLd: { breadcrumb: breadcrumbJsonLd(crumbs) },
  });

  return (
    <StorefrontLayout>
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <Breadcrumbs items={crumbs} />
        <div className="mb-12">
          <div className="text-[10px] uppercase tracking-[0.3em] text-red-500 font-bold mb-3">
            {vehicleId ? "Compatible parts" : "Catalog"}
          </div>
          <h1 className="font-display text-4xl md:text-6xl tracking-tighter font-medium" data-testid="shop-heading">
            {heading}
          </h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-3 space-y-8" data-testid="shop-filters">
            {!category && (
              <FilterGroup title="Category">
                {[{ key: "side-mirror", label: "Side Mirrors" }, { key: "windshield", label: "Windshields" }, { key: "backglass", label: "Backglass" }, { key: "sunroof", label: "Sunroof" }].map((c) => (
                  <FilterPill key={c.key} active={filters.category === c.key} onClick={() => setExclusive("category", c.key)} testid={`filter-cat-${c.key}`}>
                    {c.label}
                  </FilterPill>
                ))}
              </FilterGroup>
            )}

            <FilterGroup title="Position">
              {POSITION_FILTERS.map((p) => (
                <FilterPill key={p.key} active={filters.position === p.key} onClick={() => setExclusive("position", p.key)} testid={`filter-pos-${p.key}`}>
                  {p.label}
                </FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup title="Fitment type">
              {FITMENT_TYPE_FILTERS.map((p) => (
                <FilterPill key={p.key} active={filters.fitment_type === p.key} onClick={() => setExclusive("fitment_type", p.key)} testid={`filter-ft-${p.key}`}>
                  {p.label}
                </FilterPill>
              ))}
            </FilterGroup>

            <FilterGroup title="Features">
              {FEATURE_FILTERS.map((f) => (
                <FilterPill key={f.key} active={!!filters[f.key]} onClick={() => toggleFeature(f.key)} testid={`filter-feat-${f.key}`}>
                  {f.label}
                </FilterPill>
              ))}
            </FilterGroup>
          </aside>

          <div className="lg:col-span-9">
            <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="text-xs text-zinc-500" data-testid="shop-count">
                  {loading
                    ? "Loading…"
                    : vehicleId
                      ? `${products.length} ${products.length === 1 ? "product" : "products"}`
                      : total === 0
                        ? "0 products"
                        : <span data-testid="shop-showing-range">Showing {rangeStart}–{rangeEnd} of {total} products</span>}
                </div>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    data-testid="shop-clear-filters"
                    className="inline-flex items-center gap-1 text-xs text-red-500 hover:text-red-400 transition"
                  >
                    <X size={12} /> Clear filters
                  </button>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {!vehicleId && (
                  <>
                    <label className="flex items-center gap-2 text-xs text-zinc-500">
                      <span className="hidden sm:inline">Sort</span>
                      <select data-testid="shop-sort-select" value={sort} onChange={(e) => changeSort(e.target.value)} className={CONTROL_SEL}>
                        <option value="newest">Newest</option>
                        <option value="price_asc">Price: Low to High</option>
                        <option value="price_desc">Price: High to Low</option>
                        <option value="name">Product Name</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-xs text-zinc-500">
                      <span className="hidden sm:inline">Products per page</span>
                      <select data-testid="shop-perpage-select" value={perPage} onChange={(e) => changePerPage(Number(e.target.value))} className={CONTROL_SEL}>
                        {[12, 24, 48, 96].map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </label>
                  </>
                )}
                <FitmentAssistButton
                  variant="subtle"
                  prefill={(() => {
                    const parts = (vehicleLabel || "").split(" ");
                    const year = parts[0] && /^\d{4}$/.test(parts[0]) ? parts[0] : "";
                    const make = year ? parts[1] : parts[0];
                    const model = year ? parts.slice(2).join(" ") : parts.slice(1).join(" ");
                    return { vehicle_year: year, vehicle_make: make, vehicle_model: model };
                  })()}
                />
              </div>
            </div>

            {vehicleId && !loading && (exactProducts.length > 0 || relatedProducts.length > 0) ? (
              <div className="space-y-14">
                {exactProducts.length > 0 && (
                  <section data-testid="shop-section-exact">
                    <div className="flex items-baseline gap-3 mb-5">
                      <h2 className="font-display text-xl md:text-2xl tracking-tight font-medium">
                        Compatible Parts{vehicleLabel ? ` for ${vehicleLabel}` : ""}
                      </h2>
                      <span className="text-[10px] uppercase tracking-[0.25em] text-emerald-600 font-bold font-mono">
                        ✓ Verified Fit
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {exactProducts.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                  </section>
                )}

                {relatedProducts.length > 0 && (
                  <section data-testid="shop-section-related">
                    <div className="mb-5">
                      <h2 className="font-display text-xl md:text-2xl tracking-tight font-medium">
                        Related Parts You May Also Need
                      </h2>
                      <p className="text-xs text-zinc-500 mt-1">
                        Compatible with the same model — confirm exact trim/year fitment before ordering.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                      {relatedProducts.map((p) => <ProductCard key={p.id} product={p} />)}
                    </div>
                  </section>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {products.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            )}

            {!loading && products.length === 0 && (
              <div className="text-center text-zinc-500 py-20 border border-dashed border-white/10 rounded-md" data-testid="shop-empty">
                No products match these filters.
              </div>
            )}

            {!vehicleId && !loading && totalPages > 1 && (
              <Pagination page={page} totalPages={totalPages} onChange={goToPage} />
            )}
          </div>
        </div>
      </div>
    </StorefrontLayout>
  );
}

export function Pagination({ page, totalPages, onChange }) {
  const pages = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    pages.push(1);
    const start = Math.max(2, page - 1);
    const end = Math.min(totalPages - 1, page + 1);
    if (start > 2) pages.push("…");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < totalPages - 1) pages.push("…");
    pages.push(totalPages);
  }
  const btn = "min-w-9 h-9 px-2 text-xs rounded-sm border transition inline-flex items-center justify-center";
  return (
    <div className="mt-10 flex items-center justify-center gap-1.5 flex-wrap" data-testid="shop-pagination">
      <button
        data-testid="shop-prev-page"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className={`${btn} border-white/15 text-zinc-300 hover:border-white/40 disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Previous page"
      >
        <ChevronLeft size={14} />
      </button>
      {pages.map((n, i) =>
        n === "…" ? (
          <span key={`e${i}`} className="px-1 text-zinc-500 text-xs">…</span>
        ) : (
          <button
            key={n}
            data-testid={`shop-page-${n}`}
            onClick={() => onChange(n)}
            aria-current={n === page ? "page" : undefined}
            className={`${btn} ${n === page ? "border-red-500 bg-red-500/10 text-white font-semibold" : "border-white/15 text-zinc-300 hover:border-white/40"}`}
          >
            {n}
          </button>
        )
      )}
      <button
        data-testid="shop-next-page"
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className={`${btn} border-white/15 text-zinc-300 hover:border-white/40 disabled:opacity-40 disabled:cursor-not-allowed`}
        aria-label="Next page"
      >
        <ChevronRight size={14} />
      </button>
    </div>
  );
}

function FilterGroup({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-3">{title}</div>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}
function FilterPill({ children, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`text-xs px-3 py-2 border transition rounded-sm ${active ? "border-red-500 text-white bg-red-500/10" : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"}`}
    >
      {children}
    </button>
  );
}
